import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';

import { apiClient } from '../../state';

/**
 * Apply a space template to a space or create a new space with the template.
 */
export async function applySpaceTemplate(props: {
    /**
     * The template to apply.
     */
    template: api.SpaceTemplate;

    /**
     * Optional parameters for the template.
     */
    templateParams?: api.SpaceTemplateParams;

    /**
     * The organization ID to use for the template.
     */
    organizationId: string;

    /**
     * The space to apply the template to.
     */
    space?: api.Space | undefined;

    /**
     * The collection where the space will be created.
     */
    collectionId?: string;

    /**
     * Whether to log analytics for the template application.
     * @default true
     */
    logAnalytics?: boolean;
}) {
    const { template, space, collectionId, organizationId, logAnalytics } = props;
    if (space) {
        const [{ data: updatedSpace }] = await Promise.all([
            apiClient.spaces.updateSpaceById(space.id, {
                emoji: 'emoji' in template ? template.emoji : undefined,
                title: template.title,
            }),
            apiClient.spaceContent.applyTemplateToSpace(space.id, {
                id: template.id,
            }),
            template.openAPISpec
                ? createAndWaitForOpenAPISpec({
                      openAPISpec: template.openAPISpec,
                      organizationId,
                  })
                : null,
        ]);

        if (logAnalytics) {
            analytics.logEvent({
                type: 'edit_pick_template',
                template: template.id,
                organization: organizationId,
                space: space.id,
            });
        }

        return updatedSpace;
    }

    const [{ data: createdSpace }] = await Promise.all([
        apiClient.spaces.createSpace(props.organizationId, {
            template: {
                id: template.id,
                params: props.templateParams,
            },
            emoji: 'emoji' in template ? template.emoji : undefined,
            title: template.title,
            parent: collectionId,
        }),
        template.openAPISpec
            ? createAndWaitForOpenAPISpec({
                  openAPISpec: template.openAPISpec,
                  organizationId,
              })
            : null,
    ]);

    if (logAnalytics) {
        analytics.logEvent({
            type: 'templates_pick_template',
            template: template.id,
            organization: organizationId,
        });
    }

    return createdSpace;
}

/**
 * Create an OpenAPI reference for the template if it has one.
 */
export async function createAndWaitForOpenAPISpec(props: {
    openAPISpec: NonNullable<api.SpaceTemplate['openAPISpec']>;
    organizationId: string;
}) {
    const { openAPISpec, organizationId } = props;
    await apiClient.openapi.createOrUpdateOpenApiSpecBySlug(organizationId, openAPISpec.slug, {
        source: {
            url: openAPISpec.sourceURL,
        },
    });

    // Wait two seconds before checking the spec processing state.
    await new Promise((resolve) => setTimeout(resolve, 2000));
    await waitForSpecProcessingComplete(organizationId, openAPISpec.slug);
}

/**
 * Wait for the OpenAPI spec processing to complete.
 * Returns the spec if processing is complete, or null if it times out.
 */
async function waitForSpecProcessingComplete(
    organizationId: string,
    slug: string,
    timeoutMs = 10000,
    intervalMs = 3000
): Promise<api.OpenAPISpec | null> {
    const start = Date.now();
    while (true) {
        const spec = await apiClient.openapi.getOpenApiSpecBySlug(organizationId, slug);
        if (spec.data.processingState === api.OpenAPISpecProcessingState.Complete) {
            return spec.data;
        }
        if (Date.now() - start > timeoutMs) {
            return null;
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
}
